import React, { useEffect, useState, useMemo } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useSelector } from 'react-redux'

import {
	Typography,
	Button,
	IconButton,
	TextField,
	Toolbar,
	InputAdornment,
	Tooltip,
	CircularProgress
} from '@material-ui/core'

import { CalendarToday, ArrowBackIosOutlined, ArrowForwardIosOutlined, Search, Clear } from '@material-ui/icons'

import { KeyboardDatePicker } from '@material-ui/pickers'

import moment from 'moment-timezone'
import clsx from 'clsx'
import { FilterButton } from '../../Components/Activities/FilterButton'
import { SelectedFilters } from '../../Components/Activities/SelectedFilters'
import useSelectedFacility from '../../../../hooks/useSelectedFacility'
import useMarkets from '../../../../hooks/useMarkets'

const useStyles = makeStyles((theme) => ({
	headerButton: {
		fontFamily: 'Nunito',
		fontWeight: '700',
		'& .MuiButton-label': {
			lineHeight: '19px',
			fontSize: '14px'
		}
	},
	addClientButton: {
		color: theme.palette.secondary.main,
		borderColor: theme.palette.secondary.main,
		padding: '7px 40px 8px 24px',
		'& .MuiButton-label': {
			'& .MuiButton-startIcon': {
				marginRight: 16
			}
		}
	},
	toolbarWrapper: {
		display: 'flex',
		flexDirection: 'column',
		backgroundColor: '#F9F9F9',
		padding: 24,
		paddingRight: 21
	},
	petStatusIcon: {
		position: 'absolute',
		left: 0
	},
	toolbarItemsWrapper: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		flex: 1,
		gap: '1rem',
		'& > *': {
			marginBottom: 8
		}
	},
	toolbar: {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		paddingBottom: 16
	},
	searchField: {
		minWidth: 150
	},
	datePickerContainer: {
		display: 'flex',
		alignItems: 'center',
		position: 'relative'
	},
	datePicker: {
		color: '#FFFFFF',
		opacity: 0,
		position: 'absolute',
		top: 0,
		height: 38,
		margin: 0,
		'& *': {
			height: 0,
			padding: 0,
			margin: 0
		}
	},
	calendarButton: {
		'& svg': {
			fill: theme.palette.primary.main
		}
	},
	calendarHeaderButtons: {
		fill: theme.palette.primary.main
	},
	filterButton: {
		zIndex: 1
	},
	calendarDateButton: {
		zIndex: 1,
		border: '1px solid #D0CFD2',
		padding: '7px 16px 8px 24px',
		color: '#4D4C4F',
		fontWeight: '700',
		'& .MuiButton-label': {
			'& .MuiButton-endIcon': {
				marginLeft: 16
			},
			whiteSpace: 'nowrap'
		}
	},
	dateChangeButton: {
		border: '1px solid #D0CFD2',
		height: 38,
		width: 38,
		padding: 5,
		'& svg': {
			height: 16
		}
	},
	addAppointmentButton: {
		color: theme.palette.secondary.main,
		borderColor: theme.palette.secondary.main,
		padding: '7px 40px 8px 24px',
		'& .MuiButton-label': {
			'& .MuiButton-startIcon': {
				marginRight: 16
			}
		}
	},
	scheduleAggregatesWrapper: {
		display: 'flex',
		flexWrap: 'wrap',
		paddingTop: '10px',
		paddingBottom: '12px'
	},
	scheduleAggregate: {
		display: 'flex',
		flex: '238px 0 0',
		flexWrap: 'wrap',
		justifyContent: 'space-between',
		padding: '10px 16px',
		borderRadius: 6,
		color: '#4D4C4F',
		marginRight: 8,
		marginBottom: 8,
		background: '#F3F3F3'
	},
	membershipWrapper: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center'
	},
	membershipIcon: {
		color: '#6B4AD6',
		'&::before': {
			fontSize: '14px',
			lineHeight: '14px'
		}
	},
	membershipType: {
		fontFamily: 'Nunito',
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '19px',
		color: '#777679',
		textTransform: 'capitalize'
	},
	filterIcon: {
		fontSize: 19,
		color: '#777679',
		marginRight: 8
	},
	filterButtonLabel: {
		alignItems: 'center'
	},
	noWrap: {
		whiteSpace: 'nowrap'
	},
	tables: {
		display: 'flex',
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginBottom: 24
	}
}))

export const ActivitiesToolbar = React.forwardRef((props, ref) => {
	const classes = useStyles()

	const [search, setSearch] = useState('')
	const [isOpen, setIsOpen] = useState(false)
	const [busy, setBusy] = useState(false)

	const loading = useSelector((state) => state.specific.visits.loading)
	const { runAutoAssignCubbies, facility } = useSelectedFacility()
	const { markets } = useMarkets()

	let { tableRef, date, getTimeTables, setDate, changeDate, addOns = [] } = props

	const canAutoAssignCubbies = useMemo(() => {
		const now = moment()
		const market = markets?.find((market) => market.city === facility?.market?.city)
		if (!market) {
			console.log('Market not found. Could be an issue or a change with the facility model in redux')
		}
		const nowAtFacilityTimeZone = now.clone().tz(market?.time_zone || 'America/New_York')
		if (nowAtFacilityTimeZone.get('hour') < 7 && date === now.format('YYYY-MM-DD')) {
			return true
		}
		const tomorrow = now.clone().add(1, 'day').format('YYYY-MM-DD')
		return date === tomorrow
	}, [date, facility, markets])

	const runAutoAssignCubbiesProcess = async () => {
		try {
			setBusy(true)
			await runAutoAssignCubbies(date)
		} catch (e) {
			console.log('error assigning cubbies', e)
		}
		setBusy(false)
	}

	const updateDate = (date) => {
		setDate(moment(date).format('YYYY-MM-DD'))
		setIsOpen(false)
	}

	const onSearchChange = (searchText) => {
		tableRef.current.dataManager.changeSearchText(searchText)
		tableRef.current.onSearchChangeDebounce(searchText)
	}

	useEffect(() => {
		onSearchChange(search)
	}, [search])

	return (
		<div ref={ref} className={classes.toolbarWrapper}>
			<Toolbar className={classes.toolbar}>
				<div className={classes.toolbarItemsWrapper}>
					<Typography
						variant="h6"
						color="primary"
						style={{
							fontSize: '22px',
							fontWeight: 800,
							lineHeight: '30px',
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis'
						}}>
						Activities
					</Typography>
					<TextField
						value={search}
						className={classes.searchField}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="Search"
						variant="filled"
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Tooltip title="Search">
										<Search fontSize="small" />
									</Tooltip>
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										disabled={!search}
										onClick={() => setSearch('')}
										aria-label="Clear">
										<Clear fontSize="small" aria-label="clear" />
									</IconButton>
								</InputAdornment>
							),
							inputProps: {
								'aria-label': 'Search'
							}
						}}
					/>
					<FilterButton addOns={addOns} />
					<div className={classes.datePickerContainer}>
						<Button
							className={clsx(classes.headerButton, classes.calendarDateButton)}
							onClick={() => setIsOpen(true)}
							variant="outlined"
							endIcon={<CalendarToday style={{ height: 19, width: 19, fill: '#777679' }} />}
							classes={{
								label: classes.noWrap
							}}
							size="large">
							{moment(date).format('MMM D, YYYY')}
						</Button>
						<KeyboardDatePicker
							disableToolbar
							open={isOpen}
							onOpen={() => setIsOpen(true)}
							onClose={() => setIsOpen(false)}
							variant="inline"
							format="MM/DD/YYYY"
							margin="normal"
							id="start-date"
							label="Start Date"
							inputVariant="filled"
							value={date}
							onChange={updateDate}
							className={classes.datePicker}
							rightArrowIcon={<ArrowForwardIosOutlined className={classes.calendarHeaderButtons} />}
							leftArrowIcon={<ArrowBackIosOutlined className={classes.calendarHeaderButtons} />}
							KeyboardButtonProps={{
								'aria-label': 'Start Date',
								className: classes.calendarButton
							}}
						/>
					</div>
					<div style={{ display: 'flex' }}>
						<IconButton
							className={classes.dateChangeButton}
							onClick={() => changeDate('subtract')}
							style={{ marginRight: 8 }}>
							<ArrowBackIosOutlined style={{ fill: '#777679' }} />
						</IconButton>
						<IconButton className={classes.dateChangeButton} onClick={() => changeDate('add')}>
							<ArrowForwardIosOutlined style={{ fill: '#777679' }} />
						</IconButton>
					</div>
					{loading ? <CircularProgress size="2rem" /> : null}
					<div style={{ flex: 1 }}></div>
					<div>
						<Button
							variant="outlined"
							disabled={!canAutoAssignCubbies || busy}
							onClick={() => {
								if (canAutoAssignCubbies && !busy) {
									runAutoAssignCubbiesProcess()
								}
							}}
							style={{
								color: '#6B4AD6',
								opacity: canAutoAssignCubbies && !busy ? 1 : 0.5
							}}>
							AUTO-ASSIGN CUBBIES
						</Button>
					</div>
				</div>
			</Toolbar>
			<div className={classes.tables}>{getTimeTables()}</div>
			<div style={{ display: 'flex' }}>
				<Typography style={{ marginRight: 8 }} variant="overline">
					Activities
				</Typography>
				<SelectedFilters />
			</div>
		</div>
	)
})
